import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-06-02 15:57:23 +0200 (Tue, 02 Jun 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163464 $
//  Purpose   : overview grid (tree/id)
// =============================================================================
// ---Third-party imports
import React, { createElement } from 'react';
import { Grid } from 'semantic-ui-react';
import { isValidElementType, isElement, isFragment } from 'react-is';
import clsx from 'clsx';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { useSelector, useDimensions, useRouter, useMemo, usePhysicalNode, usePartContext } from 'uix/hooks';
import { mapItems, isArray, isString, dict, sorted, range, identity, isFunction, items } from 'ldash';
import { checkAcl } from 'uix/auth/acl';
import { DashboardPanels } from 'uix/registry';
// ---Local impors
import styles from './grid.css';
import { DebugFeature } from './debug';
import { XMenu } from './menu';
import { HeaderSegment } from './header';
import { NodeMenu } from './nodeMenu';
// ---Globals
export const RGrid = ({ children, stackOn = 800, ...props })=>{
    // goto one column mode if width is less than `stackOn`
    let [dimensions, ref] = useDimensions({
        delta: 50
    });
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        className: clsx(dimensions.width < stackOn && styles.narrow),
        children: /*#__PURE__*/ _jsx(Grid, {
            ...props,
            children: children
        })
    });
};
RGrid.Row = Grid.Row;
RGrid.Column = Grid.Column;
export const RGridNonStacking = ({ colwidth, children = [], renderWrap })=>{
    /* build a grid with variable column numbers
  derived from available width and given colwidth
  */ let [dimensions, ref] = useDimensions({
        delta: 50
    });
    let num_cols = Math.floor(dimensions.width / colwidth);
    if (!isArray(children)) return 'children needs to be a list';
    // if we have only 6 children, we allow max 3 columns
    if (children.length > 2) {
        num_cols = Math.min(num_cols, Math.floor(children.length / 2));
    }
    num_cols = Math.min(Math.max(1, num_cols), children.length);
    //~ log.x(num_cols)
    let num_items = children.length / num_cols;
    let take = (i)=>children.slice(num_items * i, num_items * (i + 1));
    renderWrap = renderWrap || identity;
    let cols = range(num_cols).map((i)=>/*#__PURE__*/ _jsx(Grid.Column, {
            children: renderWrap(take(i))
        }, i));
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        style: {
            flex: 1
        },
        children: /*#__PURE__*/ _jsx(Grid, {
            relaxed: false,
            padded: false,
            columns: num_cols,
            divided: true,
            children: /*#__PURE__*/ _jsx(Grid.Row, {
                children: cols
            })
        })
    });
};
export const DashGrid = ({ node, icon, header = 'Übersicht', panels, menu, menuPortal, width = 16, widthLeft = 8 })=>{
    /* create react elements from registered dashpanels
   */ let widthRight = width - widthLeft;
    let phys_node = usePhysicalNode;
    node = node || phys_node;
    let apiurl = useSelector(Sel.apiurl);
    let iconfig = useSelector(Sel.iconfig);
    let { go } = useRouter();
    let { i18n, user, dname, dconfig } = usePartContext();
    let subheader = null;
    let isAllowed = (acl)=>!acl || checkAcl(acl, user, dname);
    let createPanel = (name, c, props)=>{
        // create react element
        props = {
            ...props,
            key: name,
            go,
            i18n,
            node,
            apiurl,
            dconfig,
            iconfig
        };
        if (isElement(c) || isFragment(c)) {
            return /*#__PURE__*/ _jsx(React.Fragment, {
                children: c
            }, props.key);
        } else if (!isString(c) && isValidElementType(c)) {
            return /*#__PURE__*/ createElement(c, props);
        } else {
            return /*#__PURE__*/ createElement(()=>c, props);
        }
    };
    let decode_align = (cfg)=>{
        let a = isFunction(cfg.align) ? cfg.align({
            node
        }) : cfg.align;
        if (isArray(a)) return {
            position: a[0],
            order: a[1]
        };
        if (isString(a)) return {
            position: a,
            order: 0
        };
        return {
            position: 'left',
            order: 0
        };
    };
    let isActive = (name, cfg, align)=>{
        //~ log.x(name, cfg)
        let enabledCheck = (cfg)=>cfg.enabled?.({
                node,
                dconfig,
                iconfig
            }) ?? true;
        let datatypeCheck = (cfg)=>!cfg.datatypes || cfg.datatypes.includes(node.datatypeName);
        //~ log.x('%o', decode_align(cfg))
        let panel_align = decode_align(cfg).position;
        return datatypeCheck(cfg) && enabledCheck(cfg) && isAllowed(cfg.acl) && (align === undefined || panel_align === align || !panel_align && align == 'left');
    };
    let { left, right, top, bottom } = useMemo(()=>{
        if (isArray(panels)) {
            panels = dict(panels.map((cfg, i)=>{
                if (isString(cfg)) {
                    return [
                        cfg,
                        DashboardPanels[cfg] || {}
                    ];
                }
                return [
                    cfg.name || `unnamed_${i}`,
                    cfg
                ];
            }));
        }
        panels = dict(sorted(mapItems(panels, (name, cfg)=>[
                name,
                DashboardPanels[cfg.name] ? {
                    ...DashboardPanels[cfg.name],
                    ...cfg
                } : cfg
            ]), ([name, cfg])=>cfg.order));
        let getFromCfg = (align)=>{
            let sorted_panels = sorted(items(panels), (e)=>decode_align(e[1]).order);
            return sorted_panels.filter(([name, cfg])=>isActive(name, cfg, align)).map(([name, cfg])=>{
                let { Comp: c, ...props } = cfg;
                if (!c) {
                    c = `No Comp implemented for ${name}`;
                }
                return createPanel(name, c, props);
            });
        };
        return dict([
            'left',
            'right',
            'top',
            'bottom'
        ].map((e)=>[
                e,
                getFromCfg(e)
            ]));
    }, [
        node,
        user,
        panels,
        dconfig,
        iconfig
    ]);
    let one_col = left.length == 0 || right.length == 0;
    menu = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(NodeMenu, {
                node: node
            }),
            menu,
            /*#__PURE__*/ _jsx(DebugFeature, {
                children: /*#__PURE__*/ _jsx(XMenu, {
                    children: /*#__PURE__*/ _jsx(XMenu.PPrintButton, {
                        data: dict(mapItems(panels, (name, { Comp, ...cfg })=>[
                                name,
                                cfg
                            ], isActive))
                    })
                })
            })
        ]
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(HeaderSegment, {
            major: true,
            header: header,
            subheader: subheader,
            icon: icon || 'overview',
            menu: menu,
            portal: menuPortal,
            children: /*#__PURE__*/ _jsxs(RGrid, {
                relaxed: false,
                padded: false,
                stackable: true,
                children: [
                    top.length > 0 && /*#__PURE__*/ _jsx(Grid.Row, {
                        children: /*#__PURE__*/ _jsx(Grid.Column, {
                            width: width,
                            children: top
                        })
                    }),
                    one_col ? /*#__PURE__*/ _jsx(Grid.Row, {
                        children: /*#__PURE__*/ _jsxs(Grid.Column, {
                            width: width,
                            children: [
                                left,
                                right
                            ]
                        })
                    }) : /*#__PURE__*/ _jsxs(Grid.Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Grid.Column, {
                                width: widthLeft,
                                children: left
                            }),
                            /*#__PURE__*/ _jsx(Grid.Column, {
                                width: widthRight,
                                children: right
                            })
                        ]
                    }),
                    bottom.length > 0 && /*#__PURE__*/ _jsx(Grid.Row, {
                        children: /*#__PURE__*/ _jsx(Grid.Column, {
                            width: width,
                            children: bottom
                        })
                    })
                ]
            })
        })
    });
};
export const DashSegment = (props)=>/*#__PURE__*/ _jsx(HeaderSegment, {
        basic: true,
        ...props
    });
